import styled from "styled-components";
import moment from "moment-timezone";
import { RAButtonSecondary, RATabs, RATypography } from "@/components/customs";
import { FormRequestSummaryLegItem } from "@/components/molecules";
import { Row, Col, Modal, Divider, Skeleton } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import { FLIGHT_REQUEST_OPTIONS } from "@/config/constants";
import { SvgCheck } from "@/assets/icons";
import { lightTheme } from "@/themes/ThemeConfig";
import { additionalTitle, convertVariableCodeToString } from "@/utils";
import { useSelector } from "react-redux";
import { selectAuthState, selectFormatDateTimeFull } from "@/stores/slices/authSlice";

export function ModalCharterDetails({ visible = false, onClose, booking = {}, onHandleRerequest }) {
	const {
		type,
		code,
		note = "",
		legs = [],
		additional_info = {},
		aircraft_categories = [],
		created_at,
		empty_leg_invited,
		empty_leg,
	} = booking;
	const formatDTFull = useSelector(selectFormatDateTimeFull);
	const { user } = useSelector(selectAuthState);

	const _renderRerequestButton = () => {
		if (type !== "EMPTY_LEG" && user?.activated) {
			return (
				<Row gutter={[20, 20]} justify="center" align="middle" style={{ marginTop: "40px" }}>
					<Col>
						<RAButtonSecondary
							title="RE-REQUEST"
							onClick={() => onHandleRerequest(booking)}
							data-testid="charterDetailsModal_rerequestBtn"
						/>
					</Col>
				</Row>
			);
		}
	};

	const _renderBookingInfo = () => {
		return (
			<WrapperBookingInfo>
				<Row gutter={[20, 20]}>
					<Col md={7} xs={8}>
						<RATypography variant="body4" color={lightTheme.color_text_300}>
							Request Code
						</RATypography>
						<RATypography variant="sub-heading5" color={lightTheme.color_text_500}>
							{code ?? "_"}
						</RATypography>
					</Col>
					<Col md={7} xs={8}>
						<RATypography variant="body4" color={lightTheme.color_text_300}>
							Created At
						</RATypography>
						<RATypography variant="sub-heading5" color={lightTheme.color_text_500}>
							{created_at ? moment(created_at).format(formatDTFull) : "_"}
						</RATypography>
					</Col>
					<Col md={7} xs={8}>
						<RATypography variant="body4" color={lightTheme.color_text_300}>
							Flight Type
						</RATypography>
						<RATypography variant="sub-heading5" color={lightTheme.color_primary_500}>
							{type ? convertVariableCodeToString(type) : "_"}
						</RATypography>
					</Col>
				</Row>
			</WrapperBookingInfo>
		);
	};

	const _renderLegByType = () => {
		if (type === FLIGHT_REQUEST_OPTIONS.ONE_WAY) {
			return (
				<FormRequestSummaryLegItem
					key="flight-request-item-one-way"
					legs={legs}
					categories={aircraft_categories}
					formValues={{
						...legs[0],
						departure_datetime: legs[0]?.departure?.utc_datetime,
						departure_window_datetime: legs[0]?.departure_window?.utc_datetime,
					}}
				/>
			);
		}
		if (type === "EMPTY_LEG") {
			return (
				<FormRequestSummaryLegItem
					key="flight-request-item-one-way"
					legs={legs}
					categories={aircraft_categories}
					formValues={{
						...legs[0],
						departure_datetime: legs[0]?.departure?.utc_datetime,
						departure_window_datetime: legs[0]?.departure_window?.utc_datetime,
					}}
					type={type}
					aircraft={empty_leg_invited?.aircraft || empty_leg?.aircraft || {}}
				/>
			);
		}
		if (type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP) {
			const tabRenderer = [
				{
					key: "DEPARTURE",
					label: "Departure",
					render: (
						<FormRequestSummaryLegItem
							key={`flight-request-item-0`}
							legs={legs}
							categories={aircraft_categories}
							formValues={{
								...legs[0],
								departure_datetime: legs[0]?.departure?.utc_datetime,
								departure_window_datetime: legs[0]?.departure_window?.utc_datetime,
							}}
						/>
					),
				},
				{
					key: "ARRIVAL",
					label: "Arrival",
					render: (
						<FormRequestSummaryLegItem
							key={`flight-request-item-1`}
							legs={legs}
							categories={aircraft_categories}
							formValues={{
								...legs[1],
								departure_datetime: legs[1]?.departure?.utc_datetime,
								departure_window_datetime: legs[1]?.departure_window?.utc_datetime,
							}}
						/>
					),
				},
			];
			return <RATabs tabRenderer={tabRenderer} />;
		}
		if (type === FLIGHT_REQUEST_OPTIONS.MULTI_LEG) {
			const tabRenderer = legs?.map((item, index) => {
				return {
					key: `LEG ${index + 1}`,
					label: `Leg ${index + 1}`,
					render: (
						<FormRequestSummaryLegItem
							key={`flight-request-item-${index}`}
							formValues={{
								...item,
								departure_datetime: item?.departure?.utc_datetime,
								departure_window_datetime: item?.departure_window?.utc_datetime,
							}}
							legs={legs}
							categories={aircraft_categories}
						/>
					),
				};
			});
			return <RATabs tabRenderer={tabRenderer} />;
		}
		return <Skeleton style={{ margin: "12px 0" }} />;
	};

	const _renderAdditionalInfo = () => {
		return (
			<WrapperAdditionalRequest>
				<RATypography variant="sub-heading4" color="#121216">
					Additional Requests
				</RATypography>
				{!!note && (
					<RATypography variant="body3" color="#65686F">
						{note}
					</RATypography>
				)}
				<WrapperRequestServices>
					{Object.keys(additional_info).map((v) => {
						if (!!additional_info[v]) {
							return (
								<Row gutter={[12, 12]} key={`additiona-reqeust-${v}`}>
									<Col>
										<SvgCheck color={lightTheme.color_status_success_500} />
									</Col>
									<Col>
										<RATypography variant="sub-heading4" color="#222222">
											{additionalTitle[v]}
										</RATypography>
									</Col>
								</Row>
							);
						}
					})}
				</WrapperRequestServices>
			</WrapperAdditionalRequest>
		);
	};

	return (
		<StyledModal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => onClose(false)}
			footer={null}
			width="720px"
			wrapClassName="auth-wrapper"
		>
			<ContainerBookingDetails data-testid="charterDetailsModal_content">
				<Row>
					<Col md={24}>
						<WrapperContent>
							<RATypography variant="h3">Booking Details</RATypography>
						</WrapperContent>
					</Col>
				</Row>
				{_renderBookingInfo()}
				{_renderLegByType()}
				<Divider />
				{_renderAdditionalInfo()}
				{_renderRerequestButton()}
			</ContainerBookingDetails>
		</StyledModal>
	);
}

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background-color: #ffffff;
		border-radius: 24px;
		box-shadow: none;
	}
	.ant-modal-close {
		width: 28px;
		height: 28px;
		top: 22px;
		right: 22px;
		@media (max-width: 576px) {
			top: 22px;
			right: 4px;
		}
	}
	.ant-modal-body {
		overflow-y: auto;
		overflow-x: hidden;
		max-height: 80vh;
	}
`;

const ContainerBookingDetails = styled.div`
	width: 100%;
	padding: clamp(24px, 8vw, 60px) clamp(16px, 4vw, 60px);
`;
const WrapperAdditionalRequest = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;
const WrapperRequestServices = styled.div`
	margin-top: 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const WrapperBookingInfo = styled.div`
	padding: 10px 12px;
	gap: 32px;
	width: 100%;
	min-height: 62px;
	background: #f9f9f9;
	border-radius: 6px;
	margin-top: 20px;
	margin-bottom: 12px;
`;
